<template>
    <div>
        <div class="page-header">
            <div class="md-icon" @click="back">arrow_back</div>
            <div class="title">{{ $t('create') }} {{ $t('terms.person') }}</div>
            <el-button type="primary" :loading="saving" @click="save"><i class="md-icon" v-if="!saving">done</i>{{ $t('save') }}</el-button>
        </div>
        <div class="fields-list" v-if="model">
            <dynamic-field v-for="field in fields"
                           :key="field.dbName"
                           v-model="model[field.friendlyName]"
                           :metadata="field" />
        </div>
    </div>
</template>


<script lang="ts">
    import Vue from 'vue';

    import dynamicField from '@/controls/fields/dynamic-field.vue';

    import { EntityType, FieldMetadata, MetadataProvider } from '@/utilities/fields/metadata';
    import { FieldName } from '@/utilities/fields/names';
    import { Person, PersonProvider } from '@/utilities/api/persons';

    export default Vue.extend({
        name: 'create-person',
        components: {
            dynamicField
        },
        data() {
            return {
                saving: false,
                fields: [] as FieldMetadata[],
                model: null as Person | null,
            }
        },
        created() {
            this.initFields();
            this.initModel();
        },
        computed: {
            defaultOrganisationId(): number {
                return +this.$route.query.organisationId || 0;
            },
            defaultEmail(): string {
                return this.$route.query.email?.toString() || '';
            },
            defaultName(): string {
                return this.$route.query.name?.toString() || '';
            },
            defaultFirstName(): string {
                return this.defaultName.split(' ')[0] || '';
            },
            defaultMiddleName(): string {
                if (!this.fields.some(f => f.dbName == FieldName.PersonMiddleName)) return '';
                const parts = this.defaultName.split(' ');
                return parts.length > 2 ? parts.slice(1, -1).join(' ') : '';
            },
            defaultLastName(): string {
                const parts = this.defaultName.split(' ');
                if (!this.fields.some(f => f.dbName == FieldName.PersonMiddleName)) {
                    return parts.pop() || '';
                } else {
                    return parts.slice(1).join(' ');
                }
            },
            returnTo(): string {
                return this.$route.query.returnTo?.toString() || '';
            }
        },
        methods: {
            initFields() {
                const fieldNames = [...new Set([
                    FieldName.PersonOrganisationId,
                    FieldName.PersonFirstName,
                    FieldName.PersonMiddleName,
                    FieldName.PersonLastName,
                    FieldName.PersonEmail,
                    FieldName.PersonEmail2,
                    FieldName.PersonDirectPhone,
                    FieldName.PersonMobilePhone,
                    ...MetadataProvider.getEntityFields(EntityType.Person).filter(f => f.isMandatory && MetadataProvider.isFieldSupported(f)).map(f => f.dbName)
                ])];
                this.fields = fieldNames
                    .map(f => MetadataProvider.getFieldByName(f)!)
                    .filter(f => f);
            },
            initModel() {
                this.model = {
                    PersonId: 0,
                    ...Object.fromEntries(this.fields.map(f => [f.friendlyName, null])),
                    PersonOrganisationId: this.defaultOrganisationId,
                    PersonFirstName: this.defaultFirstName,
                    PersonMiddleName: this.defaultMiddleName,
                    PersonLastName: this.defaultLastName,
                    PersonEmail: this.defaultEmail,
                } as any;
            },
            async save() {
                if (!this.model) return;
                if (!MetadataProvider.validateEntity(this.model, this.fields)) return;
                this.saving = true;
                try {
                    const personId = await PersonProvider.save(this.model);
                    this.$router.push({ path: this.returnTo, query: { personId: personId.toString() } });
                } catch (e: any) {
                    console.error(e);
                    this.$notify.error({ title: this.$tc('error'), message: e.error || this.$tc('an_unexpected_error_occurred') });
                }
                this.saving = false;
            },
            back() {
                this.$router.back();
            }
        }
    });

</script>

<style scoped lang="scss">

</style>